/* eslint-disable indent */
/* eslint-disable function-paren-newline */
/* eslint-disable implicit-arrow-linebreak */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, ModalFooter } from '@folio/stripes-components';
import { FormattedMessage } from 'react-intl';
import { FaPlus } from 'react-icons/fa';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import createDecorator from 'final-form-focus';
import FormFieldsGenerator from '../../../../../../../shared/FormFieldsGenerator';
import { getNextCopyNumberForVolume } from '../../VolumeManagement/utils/volumeUtils';
import { setDefaultValues } from '../../../../../../../utils/utils';

// Create focus decorator for automatic focus on validation errors
const focusOnError = createDecorator();

const CreateSingleItemModal = ({
  open,
  onClose,
  onConfirm,
  template,
  controlledVocab,
  currentItemsCount = 0,
  holdingLocationId,
  classificationNumber = '',
  authorNumber = '',
  selectedVolume = null, // New prop for volume mode
  fields = null, // New prop to access fields for volume calculations
  instanceStatisticalCodeIds = [],
}) => {
  const formRef = useRef();

  const renderFooter = (handleSubmit, pristine, submitting) => {
    return (
      <ModalFooter>
        <Button
          buttonStyle="primary"
          type="submit"
          onClick={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          disabled={submitting}
        >
          <FormattedMessage
            id="ui-plugin-inventory-templates.createSingleItemModal.create"
            defaultMessage="Create Item"
          />
        </Button>
        <Button onClick={onClose}>
          <FormattedMessage
            id="ui-plugin-inventory-templates.modal.cancel"
            defaultMessage="Cancel"
          />
        </Button>
      </ModalFooter>
    );
  };

  // Let FormFieldsGenerator handle validation based on template requirements

  const onHandleSubmit = (values) => {
    onConfirm(values);
    onClose();
  };

  // Calculate copy number based on mode (volume or normal)
  const calculateCopyNumber = () => {
    if (selectedVolume && fields) {
      return getNextCopyNumberForVolume(fields, selectedVolume);
    }
    return currentItemsCount + 1;
  };

  // Prepare initial values for new item using the same method as direct creation
  const templateDefaults = setDefaultValues(template?.templateFields?.items);

  const initialValues = {
    // Use setDefaultValues to get proper template defaults (same as direct creation)
    ...templateDefaults,
    // Override with specific values
    holdingsRecordId: holdingLocationId,
    permanentLocation: { id: holdingLocationId },
    barcode: '', // User will input barcode
    copyNumber: calculateCopyNumber(), // Calculate based on mode
    itemLevelCallNumber: `${classificationNumber}${authorNumber}`,
    volume: selectedVolume !== 'No_Volume' ? selectedVolume || '' : '', // Set volume if in volume mode
    ...(instanceStatisticalCodeIds.length && { itemStatisticalCodeIds: instanceStatisticalCodeIds }),
  };

  // Use template as-is to preserve all field requirements and validation rules
  const preparedTemplate = template?.templateFields?.items || [];

  return (
    <Form
      onSubmit={onHandleSubmit}
      mutators={{ ...arrayMutators }}
      decorators={[focusOnError]}
      initialValues={initialValues}
      render={({ handleSubmit, pristine, submitting, values, form }) => {
        // Add form ref for external access
        formRef.current = form;

        return (
          <Modal
            open={open}
            onClose={onClose}
            footer={renderFooter(handleSubmit, pristine, submitting)}
            wrappingElement="form"
            label={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '7px',
                }}
              >
                <FaPlus size={18} />
                <FormattedMessage
                  id="ui-plugin-inventory-templates.createSingleItemModal.label"
                  defaultMessage="Create New Item"
                />
                {selectedVolume && selectedVolume !== 'No_Volume' && (
                  <span style={{ fontWeight: 'normal', color: '#666' }}>
                    - Volume: {selectedVolume}
                  </span>
                )}
              </div>
            }
            size="large"
          >
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '16px' }}>
                <div
                  style={{
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    fontSize: '14px',
                    color: '#6c757d',
                  }}
                >
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.createSingleItemModal.description"
                    defaultMessage="Fill in the item details below. Required fields are marked with an asterisk."
                  />
                </div>

                {preparedTemplate &&
                  Array.isArray(preparedTemplate) &&
                  preparedTemplate.length > 0 && (
                    <FormFieldsGenerator
                      key="create-single-item"
                      formValues={preparedTemplate}
                      controlledVocab={controlledVocab}
                    />
                  )}
              </div>
            </form>
          </Modal>
        );
      }}
    />
  );
};

CreateSingleItemModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  template: PropTypes.object,
  controlledVocab: PropTypes.object,
  currentItemsCount: PropTypes.number,
  holdingLocationId: PropTypes.string,
  classificationNumber: PropTypes.string,
  authorNumber: PropTypes.string,
  selectedVolume: PropTypes.string, // New prop for volume mode
  fields: PropTypes.object, // New prop for accessing fields in volume calculations
  instanceStatisticalCodeIds: PropTypes.array,
};

export default CreateSingleItemModal;
